/**
 * Style management mixin
 * Provides copy/paste functionality for object styles
 */
export default {
    data() {
        return {
            // Style copy/paste system
            copied_style: null,
            copied_style_type: null, // 'balloon', 'text', 'standalone_text'
            
            // Context menu
            show_context_menu: false,
            context_menu_position: { x: 0, y: 0 },
            context_menu_target: null,
        };
    },

    methods: {

        copy_object_style(object) {
            if (!object) return false;

            const object_type = object.getAttr('object_type');
            let style_data = {};

            if (object_type === 'balloon') {
                style_data = this.extract_balloon_style(object);
                this.copied_style_type = 'balloon';
            } else if (object_type === 'standalone_text') {
                const text_node = object.findOne('Text');
                style_data = {
                    text: this.extract_text_style(text_node)
                };
                this.copied_style_type = 'standalone_text';
            } else {
                return false;
            }

            this.copied_style = style_data;
            return true;
        },

        extract_balloon_style(balloon) {
            const balloon_shape = balloon.findOne('.balloon_shape');
            const balloon_text = balloon.findOne('.balloon_text') || balloon.text;
            
            const style = {
                balloon: {},
                text: {}
            };

            if (balloon_shape) {
                const dash_array = balloon_shape.dash();
                style.balloon = {
                    fill: balloon_shape.fill(),
                    stroke: balloon_shape.stroke(),
                    stroke_width: balloon_shape.strokeWidth(),
                    stroke_dash: (dash_array && dash_array.length > 0) ? dash_array.join(',') : '',
                    fill_alpha: this.balloon_fill_alpha || 1
                };
            }

            if (balloon_text) {
                style.text = this.extract_text_style(balloon_text);
            }

            return style;
        },

        extract_text_style(text_node) {
            if (!text_node) return {};

            return {
                font_family: text_node.fontFamily(),
                font_size: text_node.fontSize(),
                font_style: text_node.fontStyle(),
                fill: text_node.fill(),
                stroke: text_node.stroke(),
                stroke_width: text_node.strokeWidth(),
                line_height: text_node.lineHeight(),
                align: text_node.align()
            };
        },

        paste_style_to_object(object, skip_history = false) {
            if (!object || !this.copied_style) return false;

            const object_type = object.getAttr('object_type');

            if (object_type === 'balloon') {
                this.apply_balloon_style(object, this.copied_style);
            } else if (object_type === 'standalone_text') {
                const text_node = object.findOne('Text');
                if (text_node && this.copied_style.text) {
                    this.apply_text_style(text_node, this.copied_style.text);
                }
            }

            if (!skip_history) {
                this.redraw();
                this.push_to_history('paste_style');
            }
            return true;
        },

        apply_balloon_style(balloon, style) {
            const balloon_shape = balloon.findOne('.balloon_shape');
            const balloon_text = balloon.findOne('.balloon_text') || balloon.text;

            if (balloon_shape && style.balloon) {
                balloon_shape.fill(style.balloon.fill);
                balloon_shape.stroke(style.balloon.stroke);
                balloon_shape.strokeWidth(style.balloon.stroke_width);
                
                // Handle dash pattern properly
                if (style.balloon.stroke_dash && style.balloon.stroke_dash !== '') {
                    const dash_array = style.balloon.stroke_dash.split(',').map(n => parseFloat(n)).filter(n => !isNaN(n));
                    if (dash_array.length > 0) {
                        balloon_shape.dash(dash_array);
                    } else {
                        balloon_shape.dash([]);
                    }
                } else {
                    balloon_shape.dash([]);
                }
                
                // Update fill alpha if available
                if (style.balloon.fill_alpha !== undefined) {
                    this.balloon_fill_alpha = style.balloon.fill_alpha;
                }
            }

            if (balloon_text && style.text) {
                this.apply_text_style(balloon_text, style.text);
                
                if (typeof this.recenter_balloon_text === 'function') {
                    this.recenter_balloon_text(balloon);
                }
            }
        },

        apply_text_style(text_node, style) {
            text_node.fontFamily(style.font_family);
            text_node.fontSize(style.font_size);
            text_node.fontStyle(style.font_style);
            text_node.fill(style.fill);
            text_node.stroke(style.stroke);
            text_node.strokeWidth(style.stroke_width);
            text_node.lineHeight(style.line_height);
            text_node.align(style.align);
        },

        show_context_menu_at(x, y, target) {
            this.context_menu_target = target;
            this.context_menu_position.x = x;
            this.context_menu_position.y = y;
            this.show_context_menu = true;
            
            // Only select if target is not already in the current selection
            if (target && this.selected_object_array && this.selected_object_array.length > 1) {
                // If we have multiple objects selected and the target is one of them, don't change selection
                if (!this.selected_object_array.includes(target)) {
                    this.select_object(target);
                }
            } 
            else if (target && target !== this.selected_object) {
                // Single selection case
                this.select_object(target);
            }
        },

        hide_context_menu() {
            this.show_context_menu = false;
            this.context_menu_target = null;
        },

        handle_copy_style() {
            // Check premium restriction using pr cache
            if (!this.check_local_restriction('copy_paste_styles', 0)) {
                return;
            }

            if (this.context_menu_target) {
                this.copy_object_style(this.context_menu_target);
            } else if (this.selected_object) {
                this.copy_object_style(this.selected_object);
            }
            this.hide_context_menu();
        },

        handle_paste_style() {
            // Check premium restriction using pr cache
            if (!this.check_local_restriction('copy_paste_styles', 0)) {
                return;
            }

            // FIRST: Check for multiple selection (highest priority)
            if (this.selected_object_array && this.selected_object_array.length > 1) {
                // Apply style to all selected objects
                this.selected_object_array.forEach((obj, index) => {
                    // Skip history for all but the last object to avoid multiple history entries
                    const skip_history = index < this.selected_object_array.length - 1;
                    this.paste_style_to_object(obj, skip_history);
                });
                // Redraw and push to history after all objects are updated
                this.redraw();
                this.push_to_history('paste_style');
            }
            // SECOND: Check for context menu target
            else if (this.context_menu_target) {
                this.paste_style_to_object(this.context_menu_target);
            } 
            // THIRD: Check for single selected object
            else if (this.selected_object) {
                this.paste_style_to_object(this.selected_object);
            } 
            else {
            }
            this.hide_context_menu();
        },

        can_copy_style(object) {
            // Check premium restriction - copy/paste styles is premium only
            if (!this.user_data.is_premium) {
                return false;
            }

            if (!object) return false;
            const object_type = object.getAttr('object_type');
            return object_type === 'balloon' || object_type === 'standalone_text';
        },

        can_paste_style(object) {
            // Check premium restriction - copy/paste styles is premium only
            if (!this.user_data.is_premium) {
                return false;
            }

            if (!this.copied_style) return false;
            
            // If specific object provided, check it
            if (object) {
                return this.can_copy_style(object);
            }
            
            // If no object but there's a selection, check if any selected object can receive styles
            if (this.selected_object) {
                if (this.selected_object_array && this.selected_object_array.length > 1) {
                    // Check if any object in selection can receive styles
                    return this.selected_object_array.some(obj => this.can_copy_style(obj));
                } else {
                    return this.can_copy_style(this.selected_object);
                }
            }
            
            return false;
        }

    }
};
